/**
 * Rule-based, data-backed facts about a card.
 *
 * No scores, no stars, no editorial rankings: every statement here must be
 * derivable from stored provider data. If a value is missing, the statement is
 * simply omitted — never guessed.
 */
import {
  CardCategory,
  CARD_CATEGORY_LABEL,
  CARD_CATEGORY_TOOLTIP,
  formatPercentDE,
  getCardCategory,
  getOverdraftRate,
} from "./cardCategory";

export type CostRow = { label: string; value: string; hint?: string };
export type SuitabilityTag = { label: string; hint: string };

const num = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

export function formatEuroDE(value: number, digits = 2): string {
  return `${value.toLocaleString("de-DE", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} €`;
}

const NETWORK_LABEL: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  american_express: "American Express",
  maestro: "Maestro",
  unionpay: "UnionPay",
  discover: "Discover",
};

/** "KREDITKARTE · VISA" */
export function categoryLine(card: any): { label: string; tooltip: string; category: CardCategory } {
  const category = getCardCategory(card);
  const network = NETWORK_LABEL[String(card?.network ?? "").toLowerCase()];
  return {
    label: [CARD_CATEGORY_LABEL[category], network].filter(Boolean).join(" · "),
    tooltip: CARD_CATEGORY_TOOLTIP[category],
    category,
  };
}

export function isRealCreditCard(card: any): boolean {
  const category = getCardCategory(card);
  return category === "revolving" || category === "charge";
}

export function annualFeeLabel(card: any): string {
  const fee = num(card?.annual_fee);
  if (fee === null) return "Keine Angabe";
  return fee === 0 ? "0 €" : formatEuroDE(fee);
}

/** Foreign payment fee outside the eurozone. */
export function fxIntlPercent(card: any): number | null {
  return num(card?.fx_fee_intl_percent) ?? num(card?.foreign_tx_fee_percent);
}

export function fxLabel(card: any): string | null {
  const intl = fxIntlPercent(card);
  if (intl === null) return null;
  return intl === 0 ? "0 %" : formatPercentDE(intl);
}

export function atmLabel(card: any): string | null {
  const pct = num(card?.atm_fee_percent);
  const min = num(card?.atm_fee_min_eur);
  if (pct === null && min === null) return null;
  if ((pct ?? 0) === 0 && (min ?? 0) === 0) return "kostenlos";
  if (pct !== null && min) return `mind. ${formatEuroDE(min)} + ${formatPercentDE(pct)}`;
  if (pct !== null) return formatPercentDE(pct);
  return `mind. ${formatEuroDE(min!)}`;

}

export function hasTravelInsurance(card: any): boolean {
  return Boolean(
    card?.travel_insurance ||
      card?.travel_health_insurance ||
      card?.travel_accident_insurance ||
      card?.travel_cancellation_insurance ||
      card?.travel_baggage_insurance,
  );
}

export function needsCurrentAccount(card: any): boolean {
  return card?.current_account_included === true;
}

/**
 * The badges shown at the top of a card: the card type first, plus at most one
 * earned, data-backed highlight. Never an editorial claim.
 */
export function primaryBadges(card: any): { label: string; tooltip?: string }[] {
  const { label, tooltip } = categoryLine(card);
  const badges: { label: string; tooltip?: string }[] = [{ label, tooltip }];

  const fee = num(card?.annual_fee);
  const fx = fxIntlPercent(card);
  const atmPct = num(card?.atm_fee_percent);
  const atmMin = num(card?.atm_fee_min_eur);

  if (fee === 0) badges.push({ label: "0 € Jahresgebühr" });
  else if (fx === 0) badges.push({ label: "Keine Fremdwährungsgebühr" });
  else if ((atmPct ?? 1) === 0 && (atmMin ?? 1) === 0)
    badges.push({ label: "Weltweit gebührenfrei Bargeld" });
  else if (hasTravelInsurance(card)) badges.push({ label: "Reiseversicherung inklusive" });

  return badges;
}

/** Up to three short factual highlights shown as checkmarks. */
export function cardHighlights(card: any, max = 3): string[] {

  const out: string[] = [];
  const fee = num(card?.annual_fee);
  const fx = fxIntlPercent(card);
  const category = getCardCategory(card);

  if (fee === 0) out.push("0 € Jahresgebühr");
  if (fx === 0) out.push("Keine Fremdwährungsgebühr");
  else if (fx !== null && fx <= 1) out.push(`Nur ${formatPercentDE(fx)} Fremdwährungsgebühr`);
  if (hasTravelInsurance(card)) out.push("Reiseversicherung inklusive");
  if (category === "revolving") out.push("Echte Kreditkarte mit Teilzahlung");
  if (category === "charge") out.push("Echte Kreditkarte, monatlicher Ausgleich");
  if (!needsCurrentAccount(card)) out.push("Kein neues Girokonto nötig");
  if (card?.rental_car_insurance) out.push("Mietwagen-Vorteile");
  if (card?.nfc_contactless) out.push("Kontaktloses Bezahlen");
  if (card?.hotline_available) out.push("24/7 Notfall-Hotline");

  return out.slice(0, max);
}

/** "Gut geeignet für" — rules only, never an opinion. */
export function suitabilityTags(card: any, max = 3): SuitabilityTag[] {
  const tags: SuitabilityTag[] = [];
  const fee = num(card?.annual_fee);
  const fx = fxIntlPercent(card);
  const category = getCardCategory(card);

  if (fee === 0) tags.push({ label: "Kostenlos", hint: "Keine Jahresgebühr laut Anbieterangaben." });
  if (fx === 0) tags.push({ label: "Reisen", hint: "Keine Fremdwährungsgebühr bei Zahlungen außerhalb der Eurozone." });
  if (hasTravelInsurance(card)) tags.push({ label: "Versicherungen", hint: "Mindestens eine Reiseversicherung ist enthalten." });
  if (isRealCreditCard(card) && card?.rental_car_insurance)
    tags.push({ label: "Mietwagen", hint: "Echte Kreditkarte mit hinterlegten Mietwagen-Vorteilen." });
  if (!needsCurrentAccount(card))
    tags.push({ label: "Ohne neues Girokonto", hint: "Die Karte setzt kein neues Girokonto voraus." });
  if (category === "prepaid")
    tags.push({ label: "Guthabenbasis", hint: "Prepaid-Karte: Sie zahlen nur mit aufgeladenem Guthaben." });

  return tags.slice(0, max);
}

/** "Die wichtigsten Kosten" — rows that do not apply are omitted. */
export function costRows(card: any): CostRow[] {
  const rows: CostRow[] = [];
  const category = getCardCategory(card);

  rows.push({ label: "Jahresgebühr", value: annualFeeLabel(card) });

  const fx = fxLabel(card);
  if (fx) rows.push({ label: "Fremdwährung", value: fx, hint: "Zahlungen außerhalb der Eurozone." });

  const atm = atmLabel(card);
  if (atm) rows.push({ label: "Bargeld", value: atm, hint: "Bargeldabhebung, ungünstigster hinterlegter Fall." });

  if (category === "revolving") {
    const apr = num(card?.apr_effective) ?? num(card?.apr_nominal);
    if (apr !== null)
      rows.push({
        label: "Teilzahlung",
        value: `${formatPercentDE(apr)} p. a.`,
        hint: "Effektiver Jahreszins bei Teilzahlung.",
      });
  }

  if (category === "debit") {
    const overdraft = getOverdraftRate(card);
    if (overdraft !== null)
      rows.push({
        label: "Dispozins Girokonto",
        value: `${formatPercentDE(overdraft)} p. a.`,
        hint: "Zins des verbundenen Girokontos, kein Kartenzins.",
      });
  }

  return rows;
}

/** One honest caveat, derived from the same data. */
export function cardCaveat(card: any): string | null {
  const category = getCardCategory(card);
  const apr = num(card?.apr_effective) ?? num(card?.apr_nominal);

  if (category === "revolving" && apr !== null && apr >= 15)
    return `Hoher Sollzins bei Teilzahlung (${formatPercentDE(apr)} p. a.) – am besten den vollen Betrag ausgleichen.`;
  if (category === "debit")
    return "Debitkarte: Bei Mietwagen- oder Hotelkautionen wird sie nicht überall akzeptiert.";
  if (category === "prepaid")
    return "Prepaid-Karte: Ohne aufgeladenes Guthaben sind keine Zahlungen möglich, Kautionen sind meist nicht möglich.";
  if (needsCurrentAccount(card))
    return "Nur in Verbindung mit dem Girokonto des Anbieters erhältlich.";
  if (hasTravelInsurance(card))
    return "Reiseversicherungen gelten in der Regel nur, wenn die Reise mit dieser Karte bezahlt wurde.";

  const fx = fxIntlPercent(card);
  if (fx !== null && fx > 0)
    return `Außerhalb der Eurozone fallen ${formatPercentDE(fx)} Fremdwährungsgebühr an.`;

  return null;
}

/**
 * "Auf einen Blick" — the key facts as label/value pairs. Only stored values,
 * rows without data are omitted.
 */
export function atAGlanceRows(card: any): CostRow[] {
  const rows: CostRow[] = [];
  const category = getCardCategory(card);
  const network = NETWORK_LABEL[String(card?.network ?? "").toLowerCase()];

  rows.push({ label: "Kartentyp", value: CARD_CATEGORY_LABEL[category], hint: CARD_CATEGORY_TOOLTIP[category] });
  if (network) rows.push({ label: "Kartennetzwerk", value: network });
  if (card?.issuer) rows.push({ label: "Herausgeber", value: String(card.issuer) });

  rows.push(...costRows(card));

  rows.push({
    label: "Reiseversicherung",
    value: hasTravelInsurance(card) ? "enthalten" : "nicht enthalten",
  });
  rows.push({
    label: "Neues Girokonto nötig",
    value: needsCurrentAccount(card) ? "ja" : "nein",
  });

  return rows;
}

/**
 * "Kurzantwort" — two to three plain sentences that answer "was kostet diese
 * Karte und für wen passt sie", built only from stored values.
 */
export function quickAnswerText(card: any): string {
  const name = String(card?.display_name || card?.name || "Diese Karte");
  const category = getCardCategory(card);
  const network = NETWORK_LABEL[String(card?.network ?? "").toLowerCase()];
  const fee = num(card?.annual_fee);
  const fx = fxLabel(card);
  const atm = atmLabel(card);

  const sentences: string[] = [];

  sentences.push(
    `${name} ist eine ${CARD_CATEGORY_LABEL[category]}${network ? ` von ${network}` : ""}${
      card?.issuer ? `, herausgegeben von ${card.issuer}` : ""
    }.`,
  );

  const costs: string[] = [];
  if (fee !== null) costs.push(`Jahresgebühr ${fee === 0 ? "0 €" : formatEuroDE(fee)}`);
  if (fx) costs.push(`Fremdwährungsgebühr ${fx}`);
  if (atm) costs.push(`Bargeldabhebung ${atm}`);
  if (costs.length) sentences.push(`${costs.join(", ")}.`);

  const suited = suitabilityTags(card, 3).map((tag) => tag.label);
  if (suited.length) sentences.push(`Passend vor allem für: ${suited.join(", ")}.`);

  const caveat = cardCaveat(card);
  if (caveat) sentences.push(caveat);

  return sentences.join(" ");
}
